import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSimStore } from '../../store/useSimStore';
import { SCENARIOS, BASELINE_VENT } from '../../engine/scenarioEngine';
import { parseAgeYears } from '../../engine/ventEngine';
import { baselineVitalsForAge, alarmLimitsForAge, baselineVentForAge } from '../../data/pediatrics';
import {
  MODIFIERS,
  computeModifierTargets,
} from '../../engine/interventionEngine';
import { ScenarioPreviewPopup } from './ScenarioPreview';
import FetalControls from './FetalControls';
import { getScenarioFetal } from '../../engine/fetalEngine';
import { FETAL_PATTERNS } from '../../data/fetalPatterns';
import { getScenarioMedia } from '../../engine/mediaLibrary';
import { captureScenario, buildExport, parseImport } from '../../data/customScenarios';
import HelpTip from './HelpTip';
import styles from './ScenarioSelector.module.css';

function downloadText(name, text, mime = 'application/json') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Merge a partial airway-mechanics block ({vte, ppeak, pplat}) into ventSettings.
// No-op when `vent` is undefined so non-respiratory scenarios leave the machine
// untouched. Enforces the PEEP ≤ Pplat ≤ Ppeak ordering the vent screen expects.
function applyVent(store, vent) {
  if (!vent) return;
  const cur = useSimStore.getState().ventSettings;
  const merged = { ...cur, ...vent };
  merged.pplat = Math.min(merged.pplat, merged.ppeak);
  merged.peep = Math.min(merged.peep, merged.pplat);
  store.setVentSettings(merged);
}

// ── Tab + column definitions ───────────────────────────────────────────────────
// Each tab has an id, a display label, and an array of column definitions.
// Each column entry is { key, type: 'scenario' | 'modifier' }.
// Modifiers (persistent pathophysiological states) are placed in the column
// that best matches their primary clinical domain.

const TABS = [
  {
    id: 'periop',
    label: 'Periop',
    // Cardiac split into Arrhythmia / Ischemia; the remaining acute events are
    // grouped by the ACLS reversible-cause framework (H's & T's) where they map.
    columns: [
      {
        label: 'Arrhythmia',
        entries: [
          { key: 'rapidAfibHypotension', type: 'scenario' },
          { key: 'rapidSVTHypotension',  type: 'scenario' },
          { key: 'vtach',                type: 'scenario' },
          { key: 'tdp',                  type: 'scenario' },
          { key: 'vf',                   type: 'scenario' },
        ],
      },
      {
        label: 'Ischemia',
        entries: [
          { key: 'stElevation',  type: 'scenario' },
          { key: 'stDepression', type: 'scenario' },
          { key: 'rcaMI',        type: 'scenario' },
        ],
      },
      {
        // H's: Hypoxia · Hypovolemia · Hydrogen ion · Hypo/Hyperkalemia · Hypothermia
        label: 'Reversible — H’s',
        entries: [
          { key: 'hypoxia',      type: 'scenario' },
          { key: 'hypovolemia',  type: 'modifier' },
          { key: 'hyperkalemia', type: 'scenario' },
          { key: 'hyperkalemicArrest', type: 'scenario' },
          { key: 'hypothermia',  type: 'scenario' },
        ],
      },
      {
        // T's: Tension pneumothorax · Tamponade · Toxins · Thrombosis (PE/coronary)
        label: 'Reversible — T’s',
        entries: [
          { key: 'tensionPneumothorax',   type: 'modifier' },
          { key: 'cardiacTamponade',      type: 'scenario' },
          { key: 'pulmonaryEmbolism',     type: 'modifier' },
          { key: 'airEmbolism',           type: 'scenario' },
          { key: 'amnioticFluidEmbolism', type: 'scenario' },
          { key: 'last',                  type: 'scenario' },
          { key: 'methemoglobinemia',     type: 'scenario' },
          { key: 'carbonMonoxide',        type: 'scenario' },
        ],
      },
      {
        label: 'Airway / Other',
        entries: [
          { key: 'bronchospasm',          type: 'scenario' },
          { key: 'laryngospasmPartial',   type: 'scenario' },
          { key: 'laryngospasmComplete',  type: 'scenario' },
          { key: 'anaphylaxis',           type: 'scenario' },
          { key: 'malignantHyperthermia', type: 'modifier' },
          { key: 'pneumoperitoneum',      type: 'modifier' },
          { key: 'increasedICP',          type: 'scenario' },
          { key: 'hypotension',           type: 'scenario' },
        ],
      },
      {
        label: 'Endocrine / Metabolic',
        entries: [
          { key: 'pheoCrisis',      type: 'scenario' },
          { key: 'thyroidStorm',    type: 'scenario' },
          { key: 'myxedemaComa',    type: 'scenario' },
          { key: 'adrenalCrisis',   type: 'scenario' },
          { key: 'dka',             type: 'scenario' },
          { key: 'hypoglycemia',    type: 'scenario' },
          { key: 'carcinoidCrisis', type: 'scenario' },
          { key: 'serotoninSyndrome', type: 'scenario' },
          { key: 'nms',               type: 'scenario' },
        ],
      },
    ],
  },
  {
    id: 'obgyn',
    label: 'OB/GYN',
    columns: [
      {
        label: 'Hemorrhage',
        entries: [
          { key: 'obgynHemorrhagicShock',  type: 'scenario' },
          { key: 'obUterineAtony',         type: 'scenario' },
          { key: 'obUterineInversion',     type: 'scenario' },
          { key: 'obPlacentalAbruption',   type: 'scenario' },
          { key: 'obPlacentaAccreta',      type: 'scenario' },
          { key: 'amnioticFluidEmbolism',  type: 'scenario' },   // reuses periop definition
        ],
      },
      {
        label: 'Hypertensive / Neuro',
        entries: [
          { key: 'obgynEclampsia',      type: 'scenario' },
          { key: 'obSeizure',           type: 'scenario' },
          { key: 'obHELLP',             type: 'scenario' },
          { key: 'obMagnesiumToxicity', type: 'scenario' },
          { key: 'obgynHighSpinal',     type: 'scenario' },
          { key: 'last',                type: 'scenario' },  // reuses periop LAST definition
        ],
      },
      {
        label: 'Airway / Respiratory',
        entries: [
          { key: 'obAspiration',       type: 'scenario' },
          { key: 'obFailedIntubation', type: 'scenario' },
          { key: 'obPulmonaryEdema',   type: 'scenario' },
          { key: 'obVAE',              type: 'scenario' },
          { key: 'obCordProlapse',     type: 'scenario' },
        ],
      },
      {
        label: 'Cardiac / Arrest',
        entries: [
          { key: 'obgynSCAD',          type: 'scenario' },
          { key: 'obgynPeripartumCM',  type: 'scenario' },
          { key: 'obMaternalSepsis',   type: 'scenario' },
          { key: 'obgynCardiacArrest', type: 'scenario' },
        ],
      },
    ],
  },
  {
    id: 'nrp-pals',
    label: 'NRP/PALS',
    columns: [
      {
        label: 'Respiratory',
        entries: [
          { key: 'palsCroup',               type: 'scenario' },
          { key: 'palsAsthma',              type: 'scenario' },
          { key: 'palsPneumonia',           type: 'scenario' },
          { key: 'palsDisorderedBreathing', type: 'scenario' },
        ],
      },
      {
        label: 'Shock',
        entries: [
          { key: 'palsHypovolemicShock',  type: 'scenario' },
          { key: 'palsSepticShock',       type: 'scenario' },
          { key: 'palsCardiogenicShock',  type: 'scenario' },
          { key: 'palsTensionPneumo',     type: 'scenario' },
        ],
      },
      {
        label: 'Arrhythmia',
        entries: [
          { key: 'palsSvtStable',    type: 'scenario' },
          { key: 'palsSvtUnstable',  type: 'scenario' },
        ],
      },
      {
        label: 'Cardiac Arrest',
        entries: [
          { key: 'palsVfArrest',         type: 'scenario' },
          { key: 'palsAsystole',         type: 'scenario' },
          { key: 'palsPEA',              type: 'scenario' },
          { key: 'palsBradycardiaArrest', type: 'scenario' },
        ],
      },
    ],
  },
  {
    id: 'epic',
    label: 'Emergency Manual',
    columns: [
      {
        label: 'ACLS',
        entries: [
          { key: 'epicAsystolePEA', type: 'scenario' },
          { key: 'epicBradycardia', type: 'scenario' },
          { key: 'epicSVT',         type: 'scenario' },
          { key: 'epicVFVT',        type: 'scenario' },
        ],
      },
      {
        label: 'Airway / Respiratory',
        entries: [
          { key: 'epicAnaphylaxis',        type: 'scenario' },
          { key: 'epicBronchospasm',       type: 'scenario' },
          { key: 'epicDelayedEmergence',   type: 'scenario' },
          { key: 'epicDifficultAirway',    type: 'scenario' },
          { key: 'epicHypoxemia',          type: 'scenario' },
          { key: 'epicHighAirwayPressure', type: 'scenario' },
        ],
      },
      {
        label: 'Circulation',
        entries: [
          { key: 'epicHypotension',        type: 'scenario' },
          { key: 'epicHypertension',       type: 'scenario' },
          { key: 'epicMyocardialIschemia', type: 'scenario' },
          { key: 'epicHemorrhage',         type: 'scenario' },
          { key: 'epicPulmonaryEmbolism',  type: 'scenario' },
          { key: 'epicRightHeartFailure',  type: 'scenario' },
          { key: 'epicHighSpinal',         type: 'scenario' },
          { key: 'epicTransfusionReaction', type: 'scenario' },
          { key: 'epicTrauma',             type: 'scenario' },
        ],
      },
      {
        label: 'Metabolic & Equipment',
        entries: [
          { key: 'epicMalignantHyperthermia', type: 'scenario' },
          { key: 'epicLAST',                  type: 'scenario' },
          { key: 'epicPneumothorax',          type: 'scenario' },
          { key: 'epicOxygenFailure',         type: 'scenario' },
          { key: 'epicPowerFailure',          type: 'scenario' },
          { key: 'epicAirwayFire',            type: 'scenario' },
          { key: 'epicORFire',                type: 'scenario' },
        ],
      },
    ],
  },
];

// ── Timing constants ──────────────────────────────────────────────────────────
const HOVER_OPEN_MS    = 200;   // desktop: delay before popup opens (intentional hover)
const HOVER_CLOSE_MS   = 300;   // desktop: grace period before closing (lets mouse cross to popup)
const LONG_PRESS_MS    = 480;   // touch: ms held before preview appears
const MOVE_CANCEL_PX   = 8;     // touch: cancel long-press if finger moves this far

export default function ScenarioSelector({ store }) {
  const activeModifier    = useSimStore((state) => state.activeModifier);
  const activeScenario    = useSimStore((state) => state.activeScenario);
  const rhythm            = useSimStore((state) => state.rhythm);
  const setCapnoMorphology = useSimStore((state) => state.setCapnoMorphology);
  const setPatientInfo    = useSimStore((state) => state.setPatientInfo);
  const activeMedia       = useSimStore((state) => state.activeMedia);
  const setActiveMedia    = useSimStore((state) => state.setActiveMedia);
  const fetal             = useSimStore((state) => state.fetal);
  const setFetal          = useSimStore((state) => state.setFetal);
  const fetalMonitor      = useSimStore((state) => state.fetalMonitor);
  const setFetalMonitor   = useSimStore((state) => state.setFetalMonitor);
  const activeAidCard     = useSimStore((state) => state.activeAidCard);
  const setActiveAidCard  = useSimStore((state) => state.setActiveAidCard);
  const customScenarios      = useSimStore((state) => state.customScenarios);
  const addCustomScenario    = useSimStore((state) => state.addCustomScenario);
  const deleteCustomScenario = useSimStore((state) => state.deleteCustomScenario);
  const mergeCustomScenarios = useSimStore((state) => state.mergeCustomScenarios);
  const importInputRef = useRef(null);

  // Toggle a media item on/off on the learner monitor
  const toggleMedia = useCallback((item) => {
    const showing = activeMedia?.id !== item.id; // shown after this toggle
    setActiveMedia(showing ? item : null);
    if (showing) store.recordCaseEvent('media', `Shown: ${item.label}`);
  }, [activeMedia, setActiveMedia, store]);

  // Toggle the scenario's appropriate CTG: ON applies the mapped FHR/toco pattern
  // and reveals the fetal monitor; OFF hides the fetal monitor again.
  const isScenarioFetalOn = (patch) => fetalMonitor && !!patch && fetal.pattern === patch.pattern;
  const toggleScenarioFetal = useCallback((patch) => {
    if (fetalMonitor && fetal.pattern === patch.pattern) {
      setFetalMonitor(false);
    } else {
      setFetal(patch);           // merges — keeps the current zoom (windowSec)
      setFetalMonitor(true);
    }
  }, [fetalMonitor, fetal.pattern, setFetal, setFetalMonitor]);

  // Push (or hide) a card onto the learner monitor. `kind` distinguishes the
  // management cognitive aid from an investigation result (different styling).
  const toggleAidCard = useCallback((scenarioKey, card, index, kind) => {
    const isShown = activeAidCard?.scenario === SCENARIOS[scenarioKey]?.name
      && activeAidCard?.index === index && activeAidCard?.kind === kind;
    setActiveAidCard(isShown ? null : {
      title: card.title,
      lines: card.lines,
      kind,
      scenario: SCENARIOS[scenarioKey]?.name,
      index,
    });
    if (!isShown) store.recordCaseEvent('aid', `Aid: ${card.title}`);
  }, [activeAidCard, setActiveAidCard, store]);

  // ── Tab state (pure UI — not simulation state) ──────────────────────────────
  const [activeTab, setActiveTab] = useState('periop');

  // ── NRP/PALS patient metadata (controlled local inputs synced to store) ──────
  // Seed from the live store snapshot at mount so values survive tab switches.
  // We don't subscribe to patientAge/Weight — the inputs own the display value;
  // the store is the authoritative sink, not the source of re-renders here.
  const [localAge,    setLocalAge]    = useState(() => useSimStore.getState().patientAge    ?? '');
  const [localWeight, setLocalWeight] = useState(() => {
    const w = useSimStore.getState().patientWeight;
    return w != null ? String(w) : '';
  });

  const handleAgeChange = useCallback((e) => {
    const val = e.target.value;
    setLocalAge(val);
    // Read live store weight rather than a stale closure value
    setPatientInfo(val, useSimStore.getState().patientWeight);
  }, [setPatientInfo]);

  const handleWeightChange = useCallback((e) => {
    const val = e.target.value;
    setLocalWeight(val);
    const num = val === '' ? null : parseFloat(val);
    setPatientInfo(useSimStore.getState().patientAge, isNaN(num) ? null : num);
  }, [setPatientInfo]);

  const handlePatientClear = useCallback(() => {
    setLocalAge('');
    setLocalWeight('');
    setPatientInfo(null, null);
  }, [setPatientInfo]);

  // ── Preview state ───────────────────────────────────────────────────────────
  const [preview, setPreview] = useState(null); // { key, type, anchorRect }

  const closePreview = useCallback(() => setPreview(null), []);

  // ── Desktop hover refs ──────────────────────────────────────────────────────
  // Two separate timers: one to open (delay), one to close (grace period).
  // The close timer is shared with the popup — mouseenter on the popup cancels
  // it so the popup stays alive while the mouse moves from button → popup.
  const openTimerRef  = useRef(null);
  const closeTimerRef = useRef(null);

  const cancelClose = useCallback(() => clearTimeout(closeTimerRef.current), []);

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setPreview(null), HOVER_CLOSE_MS);
  }, []);

  const handleMouseEnter = useCallback((key, type, el) => {
    // Cancel any pending close (mouse came back onto a button)
    cancelClose();
    // Schedule open — replaces any previous open timer (fast cursor sweeps)
    clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => {
      setPreview({ key, type, anchorRect: el.getBoundingClientRect() });
    }, HOVER_OPEN_MS);
  }, [cancelClose]);

  const handleMouseLeave = useCallback(() => {
    // Cancel open if cursor left before the delay elapsed
    clearTimeout(openTimerRef.current);
    // Schedule close with grace period — popup mouseenter will cancel this
    scheduleClose();
  }, [scheduleClose]);

  // ── Touch / long-press refs ─────────────────────────────────────────────────
  const touchTimerRef    = useRef(null);
  const touchStartRef    = useRef(null);   // { x, y }
  const longPressActive  = useRef(false);  // true once timer fires
  const suppressClick    = useRef(false);  // block the click that follows touchend

  const handleTouchStart = useCallback((key, type, el, e) => {
    // Only single-touch; ignore multi-touch (pinch/zoom)
    if (e.touches.length !== 1) return;
    longPressActive.current = false;
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };

    clearTimeout(touchTimerRef.current);
    touchTimerRef.current = setTimeout(() => {
      longPressActive.current = true;
      // Snap the rect at fire time — button may have scrolled
      setPreview({ key, type, anchorRect: el.getBoundingClientRect() });
    }, LONG_PRESS_MS);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchTimerRef.current || !touchStartRef.current) return;
    const t = e.touches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    if (dx * dx + dy * dy > MOVE_CANCEL_PX * MOVE_CANCEL_PX) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    clearTimeout(touchTimerRef.current);
    touchTimerRef.current = null;
    if (longPressActive.current) {
      // Suppress the synthetic click that fires ~300 ms after touchend
      suppressClick.current = true;
      longPressActive.current = false;
    }
  }, []);

  // ── Scenario / modifier activation ────────────────────────────────────────
  const handleScenario = useCallback((scenarioKey) => {
    if (suppressClick.current) { suppressClick.current = false; return; }
    closePreview();
    const targetKey =
      activeScenario === scenarioKey && scenarioKey !== 'normal'
        ? 'normal'
        : scenarioKey;
    // Switching scenarios clears any cognitive-aid card pushed to the monitor.
    store.setActiveAidCard(null);
    // Resolve from built-in scenarios OR the instructor's custom library.
    const scenario = SCENARIOS[targetKey] ?? customScenarios.find((c) => c.key === targetKey);
    if (scenario) {
      store.pushUndo();
      store.setRhythm(scenario.rhythm || 'normal');
      store.setCapnoMorphology(scenario.capnoMorphology || 'normal');
      store.setActiveScenario(
        targetKey === 'normal' && scenarioKey !== 'normal' ? null : targetKey
      );
      store.applyVitals(scenario.vitals);
      // Airway mechanics → ventilator screen. Only scenarios that define a `vent`
      // block touch the machine (the `normal` scenario carries BASELINE_VENT, so
      // toggling a respiratory scenario off restores baseline). Non-respiratory
      // scenarios leave the instructor's manual vent setup untouched.
      applyVent(store, scenario.vent);
      // Returning to baseline (Normal Baseline, or toggling any scenario off)
      // restores the spontaneously breathing, non-invasively monitored view:
      // ventilator off and only ECG / SpO₂ / NIBP shown. The instructor re-reveals
      // invasive channels as a new case escalates.
      // Airway status. A scenario dictates a mechanically ventilated (intubated)
      // patient when it carries a `vent` airway-mechanics block — unless it opts
      // out with an explicit `intubated: false` (spontaneously breathing despite
      // showing airway data). Scenarios that say nothing leave the current airway
      // status untouched (the instructor may have already intubated).
      if (targetKey === 'normal') {
        store.setIntubated(false);
      } else if (scenario.intubated !== undefined) {
        store.setIntubated(scenario.intubated);
      } else if (scenario.vent) {
        store.setIntubated(true);
      }
      if (targetKey === 'normal') {
        // Age-appropriate baseline: a paediatric patient resets to PALS-normal
        // vitals, alarm limits and a weight-based ventilator setup, not adult ones.
        const age = parseAgeYears(useSimStore.getState().patientAge);
        const pedsVitals = baselineVitalsForAge(age);
        if (pedsVitals) store.applyVitals(pedsVitals);
        const limits = alarmLimitsForAge(age);
        if (limits) Object.entries(limits).forEach(([k, l]) => store.setAlarmThresholds(k, l));
        applyVent(store, baselineVentForAge(age));
        store.setSplitScreen(false);
        store.setVisibleChannels({ abp: false, cvp: false, etco2: false, temp: false, tof: false, bis: false });
        // Cancel every running intervention (and its auto-revert timer) so a
        // baseline reset really is a clean slate, not a patient still on drugs.
        store.resetInterventions();
        // Interventions are cancelled without their revert, so switch the
        // vaporizer / N₂O off explicitly — a baseline patient breathes no agent.
        store.setVentSettings({ ...useSimStore.getState().ventSettings, agent: 'none', agentPct: 0, n2o: 0 });
      } else if (scenario.channels) {
        // Scenario reveals the channels its key findings live on (e.g. tamponade
        // → arterial pulsus paradoxus + elevated CVP). Instructor can still toggle.
        Object.entries(scenario.channels).forEach(([c, visible]) => store.setChannelVisible(c, visible));
      }
      store.recordCaseEvent('scenario', scenario.name);
    }
  }, [activeScenario, store, closePreview, customScenarios]);

  // Toggle the active Monomorphic VT between "with a pulse" (unstable VT) and
  // "pulseless VT" (organised wide-complex ECG, NO perfusion — a shockable
  // arrest). Only the rhythm + haemodynamic vitals change; the scenario stays
  // active so the card + this chip remain visible.
  const togglePulselessVT = useCallback(() => {
    const s = useSimStore.getState();
    if (s.rhythm === 'vtach_pulseless') {
      s.setRhythm('vtach');
      s.applyVitals({ SpO2: 85, NIBP_Sys: 75, NIBP_Dia: 50, EtCO2: 28, ABP_Sys: 72, ABP_Dia: 48, CVP: 8 });
      s.recordCaseEvent('scenario', 'Monomorphic VT — pulse present');
    } else {
      s.setRhythm('vtach_pulseless');
      s.applyVitals({ SpO2: 40, NIBP_Sys: 0, NIBP_Dia: 0, EtCO2: 12, ABP_Sys: 0, ABP_Dia: 0, CVP: 0 });
      s.recordCaseEvent('scenario', 'Pulseless VT');
    }
  }, []);

  // Open the /replay screen in a new tab. When `withCurrentCase` is true, hand
  // off the recorded case via localStorage (cross-tab) so it loads immediately;
  // otherwise the replay screen prompts to load a saved .json/.csv file.
  const openReplay = useCallback((withCurrentCase) => {
    if (withCurrentCase) {
      const rec = useSimStore.getState().caseRecording;
      try {
        localStorage.setItem('sim.replayCase', JSON.stringify({
          startedAt: rec.startedAt,
          endedAt: rec.endedAt,
          durationMs: (rec.endedAt || Date.now()) - (rec.startedAt || Date.now()),
          samples: rec.samples,
          events: rec.events,
        }));
      } catch { /* quota / private mode — the replay screen's file loader still works */ }
    }
    window.open('/replay', '_blank', 'noopener');
  }, []);

  // ── Custom scenario actions ────────────────────────────────────────────────
  const handleSaveCurrent = useCallback(() => {
    const name = window.prompt('Name this scenario (captures the current vitals, rhythm, channels & ventilator):');
    if (name == null || !name.trim()) return;
    addCustomScenario(captureScenario(name, useSimStore.getState()));
  }, [addCustomScenario]);

  const handleExport = useCallback(() => {
    if (customScenarios.length === 0) { window.alert('No custom scenarios to export yet.'); return; }
    const stamp = new Date().toISOString().replace(/[:T]/g, '').slice(0, 15);
    downloadText(`sim_scenarios_${stamp}.json`, buildExport(customScenarios));
  }, [customScenarios]);

  const handleImportFile = useCallback((e) => {
    const file = e.target.files?.[0];
    e.target.value = '';                 // allow re-importing the same file later
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const { scenarios, errors } = parseImport(String(reader.result));
      if (scenarios.length) mergeCustomScenarios(scenarios);
      const msg = [];
      if (scenarios.length) msg.push(`Imported ${scenarios.length} scenario${scenarios.length > 1 ? 's' : ''}.`);
      if (errors.length) msg.push(`Skipped ${errors.length}:\n• ${errors.join('\n• ')}`);
      if (msg.length) window.alert(msg.join('\n\n'));
    };
    reader.readAsText(file);
  }, [mergeCustomScenarios]);

  const handleModifierToggle = useCallback(
    (modifierKey) => {
      if (suppressClick.current) { suppressClick.current = false; return; }
      closePreview();
      const isActive = activeModifier === modifierKey;
      const sign = isActive ? -1 : 1;
      store.pushUndo();
      const { vitals } = useSimStore.getState();
      const targets = computeModifierTargets(vitals, modifierKey, sign);
      store.applyVitals(targets);
      store.setActiveModifier(isActive ? null : modifierKey);

      const modifier = MODIFIERS[modifierKey];
      store.recordCaseEvent('modifier', `${modifier?.name || modifierKey} ${isActive ? 'off' : 'on'}`);
      if (modifier?.capnoMorphology) {
        setCapnoMorphology(isActive ? 'normal' : modifier.capnoMorphology);
      }
      // Airway mechanics: apply the modifier's vent on activate, restore baseline
      // on deactivate (modifiers carry absolute vent targets, like scenarios).
      if (modifier?.vent) {
        applyVent(store, isActive ? BASELINE_VENT : modifier.vent);
      }
    },
    [store, activeModifier, setCapnoMorphology, closePreview]
  );

  // Scenario requests from outside this component (the sticky footer's ↺ Baseline).
  const scenarioRequest = useSimStore((s) => s.scenarioRequest);
  const lastRequestRef = useRef(0);
  useEffect(() => {
    if (!scenarioRequest || scenarioRequest.n === lastRequestRef.current) return;
    lastRequestRef.current = scenarioRequest.n;
    handleScenario(scenarioRequest.key);
  }, [scenarioRequest, handleScenario]);

  const currentTab = TABS.find((t) => t.id === activeTab) ?? TABS[0];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <div className={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tabButton} ${activeTab === tab.id ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        {/* Instructor-built custom scenarios. */}
        <button
          className={`${styles.tabButton} ${activeTab === 'custom' ? styles.tabButtonActive : ''}`}
          onClick={() => setActiveTab('custom')}
        >
          Custom{customScenarios.length ? ` (${customScenarios.length})` : ''}
        </button>
        {/* Case replay / debrief. */}
        <button
          className={`${styles.tabButton} ${activeTab === 'replay' ? styles.tabButtonActive : ''}`}
          onClick={() => setActiveTab('replay')}
        >
          ▷ Replay
        </button>
        {/* Reset to baseline — kept out of the clinical taxonomy but one tap away. */}
        <button
          className={styles.resetButton}
          onClick={() => handleScenario('normal')}
          title="Reset all vitals to Normal Baseline"
        >
          ↺ Normal Baseline
        </button>
      </div>

      {/* ── Sub-buttons — shown when an EPIC scenario is active ─────────────── */}
      {activeScenario && (SCENARIOS[activeScenario]?.aid || SCENARIOS[activeScenario]?.investigations) && (
        <div className={styles.aidPanel}>
          <div className={styles.aidHead}>
            <span className={styles.aidTitle}>{SCENARIOS[activeScenario].name} — push to monitor<HelpTip label="About cognitive aids and investigations">Tap a button to show that cognitive aid card or investigation result (X-ray, POCUS, labs) full-screen on the learner monitor. Tap it again or use <strong>Hide card</strong> to remove it.</HelpTip></span>
            {activeAidCard && (
              <button className={styles.aidHide} onClick={() => setActiveAidCard(null)}>
                Hide card ✕
              </button>
            )}
          </div>

          {SCENARIOS[activeScenario].aid && (
            <div className={styles.aidGroup}>
              <span className={styles.aidGroupLabel}>📖 Cognitive Aid</span>
              <div className={styles.aidButtons}>
                {SCENARIOS[activeScenario].aid.map((card, i) => {
                  const shown = activeAidCard?.scenario === SCENARIOS[activeScenario].name
                    && activeAidCard?.index === i && activeAidCard?.kind === 'aid';
                  return (
                    <button
                      key={`aid-${card.title}`}
                      className={`${styles.aidBtn} ${shown ? styles.aidBtnActive : ''}`}
                      onClick={() => toggleAidCard(activeScenario, card, i, 'aid')}
                    >
                      {card.title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {SCENARIOS[activeScenario].investigations && (
            <div className={styles.aidGroup}>
              <span className={styles.aidGroupLabel}>🔬 Investigations — reveal results</span>
              <div className={styles.aidButtons}>
                {SCENARIOS[activeScenario].investigations.map((card, i) => {
                  const shown = activeAidCard?.scenario === SCENARIOS[activeScenario].name
                    && activeAidCard?.index === i && activeAidCard?.kind === 'investigation';
                  return (
                    <button
                      key={`inv-${card.title}`}
                      className={`${styles.aidBtn} ${styles.invBtn} ${shown ? styles.invBtnActive : ''}`}
                      onClick={() => toggleAidCard(activeScenario, card, i, 'investigation')}
                    >
                      {card.title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Patient info form — NRP/PALS tab only ────────────────────────── */}
      {currentTab.id === 'nrp-pals' && (
        <div className={styles.patientInfoSection}>
          <span className={styles.patientInfoTitle}>Patient</span>
          <div className={styles.patientInfoFields}>
            <div className={styles.patientInfoField}>
              <label className={styles.patientInfoLabel} htmlFor="pt-age">Age</label>
              <input
                id="pt-age"
                type="text"
                className={styles.patientInfoInput}
                placeholder="e.g. 28 weeks, 6 months, 4 years"
                value={localAge}
                onChange={handleAgeChange}
              />
            </div>
            <div className={styles.patientInfoField}>
              <label className={styles.patientInfoLabel} htmlFor="pt-weight">Weight (kg)</label>
              <input
                id="pt-weight"
                type="number"
                min="0.3"
                max="200"
                step="0.1"
                className={styles.patientInfoInput}
                placeholder="kg"
                value={localWeight}
                onChange={handleWeightChange}
              />
            </div>
            {(localAge || localWeight) && (
              <button className={styles.patientInfoClear} onClick={handlePatientClear}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Replay tab ────────────────────────────────────────────────────── */}
      {activeTab === 'replay' ? (
        (() => {
          const hasCase = (store.caseRecording?.samples?.length ?? 0) > 0;
          return (
            <div className={styles.customPanel}>
              <div className={styles.customActions}>
                <button
                  className={styles.customActionBtn}
                  onClick={() => openReplay(true)}
                  disabled={!hasCase}
                  title={hasCase ? 'Replay the case recorded this session' : 'Record a case first (Case Recording, top)'}
                >
                  ▷ Replay Current Case
                </button>
                <button className={styles.customActionBtn} onClick={() => openReplay(false)}>
                  ⤒ Open Replay Screen (load file)
                </button>
              </div>
              <div className={styles.emptyTab}>
                <span className={styles.emptyTabText}>
                  Replay scrubs a recorded case on a timeline — jump between interventions,
                  drugs, shocks and deterioration for debrief / simulation-based M&amp;M.
                  {hasCase
                    ? ' “Replay Current Case” opens the case recorded this session in a new tab.'
                    : ' Record a case with Case Recording (top), or open the replay screen and load a saved .json / .csv file.'}
                </span>
              </div>
            </div>
          );
        })()
      ) : /* ── Custom scenarios tab ──────────────────────────────────────────── */
      activeTab === 'custom' ? (
        <div className={styles.customPanel}>
          <div className={styles.customActions}>
            <button className={styles.customActionBtn} onClick={handleSaveCurrent}>＋ Save Current State</button>
            <button className={styles.customActionBtn} onClick={() => importInputRef.current?.click()}>⤒ Import Scenario</button>
            <button className={styles.customActionBtn} onClick={handleExport}>⤓ Export All</button>
            <input ref={importInputRef} type="file" accept=".json,application/json" hidden onChange={handleImportFile} />
          </div>
          {customScenarios.length === 0 ? (
            <div className={styles.emptyTab}>
              <span className={styles.emptyTabText}>
                No custom scenarios yet. Dial in the vitals, rhythm, monitor channels and
                ventilator you want, then “Save Current State” — or Import a shared .json file.
              </span>
            </div>
          ) : (
            <div className={styles.customGrid}>
              {customScenarios.map((c) => (
                <div key={c.key} className={styles.customItem}>
                  <button
                    className={`${styles.scenarioButton} ${activeScenario === c.key ? styles.scenarioButtonActive : ''}`}
                    onMouseEnter={(e) => handleMouseEnter(c.key, 'custom', e.currentTarget)}
                    onMouseLeave={handleMouseLeave}
                    onTouchStart={(e) => handleTouchStart(c.key, 'custom', e.currentTarget, e)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                    onClick={() => handleScenario(c.key)}
                  >
                    {c.name}
                  </button>
                  <button
                    className={styles.customDelete}
                    title={`Delete "${c.name}"`}
                    onClick={() => { if (window.confirm(`Delete custom scenario "${c.name}"?`)) deleteCustomScenario(c.key); }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : /* ── Column grid (active tab) ──────────────────────────────────── */
      currentTab.columns.length > 0 ? (
        <>
        {activeTab === 'obgyn' && <FetalControls />}
        <div className={styles.categoryGrid}>
          {currentTab.columns.map((col) => (
            <div key={col.label} className={styles.category}>
              <span className={styles.categoryLabel}>{col.label}</span>
              {col.entries.map(({ key, type }) => {
                const isModifier = type === 'modifier';
                const isActive = isModifier
                  ? activeModifier === key
                  : activeScenario === key;
                const label = isModifier
                  ? MODIFIERS[key]?.shortName ?? MODIFIERS[key]?.name
                  : SCENARIOS[key]?.name;
                const media = getScenarioMedia(key);

                return (
                  <Fragment key={key}>
                    <button
                      className={`${styles.scenarioButton} ${isActive ? styles.scenarioButtonActive : ''}`}
                      // ── Desktop hover ────────────────────────────────────
                      onMouseEnter={(e) => handleMouseEnter(key, type, e.currentTarget)}
                      onMouseLeave={handleMouseLeave}
                      // ── Touch / long-press ───────────────────────────────
                      onTouchStart={(e) => handleTouchStart(key, type, e.currentTarget, e)}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      onTouchCancel={handleTouchEnd}
                      // ── Activation click ─────────────────────────────────
                      onClick={() =>
                        isModifier ? handleModifierToggle(key) : handleScenario(key)
                      }
                    >
                      {label}
                    </button>

                    {/* Diagnostic media chips — shown only while this scenario is active */}
                    {isActive && media.length > 0 && (
                      <div className={styles.mediaRow}>
                        {media.map((item) => (
                          <button
                            key={item.id}
                            className={`${styles.mediaChip} ${activeMedia?.id === item.id ? styles.mediaChipActive : ''}`}
                            onClick={() => toggleMedia(item)}
                            title={`Show ${item.label} on monitor`}
                          >
                            {item.label}
                            <span className={styles.mediaChipState}>
                              {activeMedia?.id === item.id ? 'SHOWING' : 'SHOW'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Pulseless VT toggle — only on the Monomorphic VT card */}
                    {isActive && key === 'vtach' && (() => {
                      const on = rhythm === 'vtach_pulseless';
                      return (
                        <div className={styles.mediaRow}>
                          <button
                            className={`${styles.mediaChip} ${on ? styles.mediaChipActive : ''}`}
                            onClick={togglePulselessVT}
                            title={on
                              ? 'Currently pulseless (arrest). Click to restore a pulse.'
                              : 'Make this pulseless VT — flatlines pleth/art/CVP (shockable arrest)'}
                          >
                            ⚡ Pulseless VT
                            <span className={styles.mediaChipState}>{on ? 'PULSELESS' : 'HAS PULSE'}</span>
                          </button>
                        </div>
                      );
                    })()}

                    {/* CTG toggle — appropriate fetal tracing for this OB scenario */}
                    {isActive && (() => {
                      const patch = getScenarioFetal(key);
                      if (!patch) return null;
                      const on = isScenarioFetalOn(patch);
                      const label = FETAL_PATTERNS[patch.pattern]?.name ?? 'CTG';
                      return (
                        <div className={styles.mediaRow}>
                          <button
                            className={`${styles.mediaChip} ${on ? styles.mediaChipActive : ''}`}
                            onClick={() => toggleScenarioFetal(patch)}
                            title={`Show the appropriate fetal CTG (${label}) beside the monitor`}
                          >
                            🍼 CTG: {label}
                            <span className={styles.mediaChipState}>{on ? 'SHOWING' : 'SHOW'}</span>
                          </button>
                        </div>
                      );
                    })()}
                  </Fragment>
                );
              })}
            </div>
          ))}
        </div>
        </>
      ) : (
        <div className={styles.emptyTab}>
          <span className={styles.emptyTabText}>
            No scenarios yet — coming soon.
          </span>
        </div>
      )}

      {/* Portal popup — rendered outside the grid to avoid clipping */}
      {preview && (
        <ScenarioPreviewPopup
          itemKey={preview.key}
          type={preview.type}
          customScenarios={customScenarios}
          anchorRect={preview.anchorRect}
          onClose={closePreview}
          onPopupMouseEnter={cancelClose}
          onPopupMouseLeave={scheduleClose}
        />
      )}
    </>
  );
}
